<template>
  <div class="py-12 bg-white">
    <div class="max-w-5xl mx-auto px-4">
      <!-- Header -->
      <div class="text-center mb-6">
        <h2 class="text-3xl font-light text-primary mb-2">
          {{ resolvedContent.title }}
        </h2>
        <p class="text-gray-600">
          {{ resolvedContent.subtitle }}
          <a
            v-if="resolvedContent.linkText"
            :href="resolvedContent.linkUrl || '#'"
            class="text-primary font-semibold hover:underline ml-1"
          >
            {{ resolvedContent.linkText }} &rarr;
          </a>
        </p>
      </div>

      <!-- Tabs -->
      <div v-if="isVideoTabVisible" class="flex justify-center border-b border-gray-200 mb-6">
        <button
          class="px-6 py-2 text-sm font-medium transition-colors"
          :class="
            activeTab === 'google'
              ? 'text-gray-800 border-b-2 border-primary'
              : 'text-gray-500 hover:text-gray-700'
          "
          @click="activeTab = 'google'"
        >
          Google vélemények
        </button>
        <button
          class="px-6 py-2 text-sm font-medium transition-colors"
          :class="
            activeTab === 'video'
              ? 'text-gray-800 border-b-2 border-primary'
              : 'text-gray-500 hover:text-gray-700'
          "
          @click="activeTab = 'video'"
        >
          Videós vélemények
        </button>
      </div>

      <!-- Google Reviews -->
      <div v-show="activeTab === 'google'" class="relative">
        <div
          ref="googleScroll"
          class="flex gap-4 overflow-x-auto pb-4 scroll-smooth hide-scrollbar"
        >
          <div
            v-for="(review, i) in resolvedContent.googleReviews"
            :key="i"
            class="min-w-[300px] max-w-[340px] flex-shrink-0 border border-gray-200 rounded-lg p-4 flex flex-col gap-3"
          >
            <div class="flex items-center gap-3">
              <img
                v-if="review.imageUrl"
                v-lazy-load :data-src="review.imageUrl"
                :alt="review.name"
                class="w-10 h-10 rounded-full object-cover"
              />
              <div
                v-else
                class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400"
              >
                <svg
                  class="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"
                  />
                </svg>
              </div>
              <div>
                <div class="font-bold text-sm">{{ review.name }}</div>
                <div class="text-xs text-gray-500">Felhasználó</div>
              </div>
            </div>
            <div class="font-bold text-sm">{{ review.title }}</div>
            <p class="text-sm text-gray-600 leading-relaxed">
              {{ review.text }}
            </p>
            <div class="flex gap-0.5 mt-auto">
              <svg
                v-for="s in 5"
                :key="s"
                class="w-5 h-5 text-amber-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                />
              </svg>
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <div class="flex items-center gap-2 mt-4">
          <button
            class="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-gray-400 hover:text-gray-600"
            @click="scroll('google', -1)"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            class="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90"
            @click="scroll('google', 1)"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div class="flex-1 h-0.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              class="h-full bg-primary rounded-full transition-all"
              :style="{ width: googleProgress + '%' }"
            />
          </div>
        </div>
      </div>

      <!-- Video Reviews -->
      <div v-if="isVideoTabVisible" v-show="activeTab === 'video'" class="relative">
        <div
          ref="videoScroll"
          class="flex gap-4 overflow-x-auto pb-4 scroll-smooth hide-scrollbar"
        >
          <div
            v-for="(video, i) in resolvedContent.videoReviews"
            :key="i"
            class="min-w-[350px] flex-shrink-0"
          >
            <!-- Video embed or placeholder -->
            <div
              v-if="video.videoUrl"
              class="rounded-lg aspect-video overflow-hidden mb-3"
            >
              <iframe
                v-lazy-load :data-src="getEmbedUrl(video.videoUrl)"
                class="w-full h-full"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              />
            </div>
            <div
              v-else
              class="bg-gray-200 rounded-lg aspect-video flex items-center justify-center mb-3 cursor-pointer group"
            >
              <div
                class="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center group-hover:bg-red-700 transition-colors"
              >
                <svg
                  class="w-6 h-6 text-white ml-1"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div
                  class="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-400"
                >
                  <svg
                    class="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"
                    />
                  </svg>
                </div>
                <div>
                  <div class="font-bold text-sm">{{ video.name }}</div>
                  <div class="text-xs text-gray-500">Hitelesített</div>
                </div>
              </div>
              <span class="text-sm text-gray-400">Google</span>
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <div class="flex items-center gap-2 mt-4">
          <button
            class="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-gray-400 hover:text-gray-600"
            @click="scroll('video', -1)"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            class="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90"
            @click="scroll('video', 1)"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div class="flex-1 h-0.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              class="h-full bg-primary rounded-full transition-all"
              :style="{ width: videoProgress + '%' }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from "@nuxtjs/composition-api";
import { DEFAULT_MODULE_CONTENT } from "~/config/frontPageModuleDefaultContent";

export default {
  name: "ReviewsSection",
  props: {
    content: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const defaults = DEFAULT_MODULE_CONTENT.reviews || {};
    const resolvedContent = computed(() => {
      if (!props.content) return defaults;
      return { ...defaults, ...props.content };
    });

    const isVideoTabVisible = computed(() => {
      const val = resolvedContent.value.showVideoTab;
      return val === "true" || val === true || val === undefined;
    });

    const getEmbedUrl = (url) => {
      if (!url) return "";
      // YouTube
      const ytMatch = url.match(
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/
      );
      if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
      // Already an embed URL
      if (url.includes("/embed")) return url;
      return url;
    };

    const activeTab = ref("google");
    const googleScroll = ref(null);
    const videoScroll = ref(null);
    const googleProgress = ref(20);
    const videoProgress = ref(20);

    const updateProgress = (type) => {
      const el =
        type === "google" ? googleScroll.value : videoScroll.value;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      const pct = max > 0 ? (el.scrollLeft / max) * 80 + 20 : 20;
      if (type === "google") {
        googleProgress.value = pct;
      } else {
        videoProgress.value = pct;
      }
    };

    const scroll = (type, dir) => {
      const el =
        type === "google" ? googleScroll.value : videoScroll.value;
      if (!el) return;
      el.scrollBy({ left: dir * 360, behavior: "smooth" });
    };

    let googleTicking = false;
    let videoTicking = false;
    const onGoogleScroll = () => {
      if (!googleTicking) {
        googleTicking = true;
        requestAnimationFrame(() => {
          updateProgress("google");
          googleTicking = false;
        });
      }
    };
    const onVideoScroll = () => {
      if (!videoTicking) {
        videoTicking = true;
        requestAnimationFrame(() => {
          updateProgress("video");
          videoTicking = false;
        });
      }
    };

    onMounted(() => {
      if (googleScroll.value) {
        googleScroll.value.addEventListener("scroll", onGoogleScroll);
      }
      if (videoScroll.value) {
        videoScroll.value.addEventListener("scroll", onVideoScroll);
      }
    });

    onBeforeUnmount(() => {
      if (googleScroll.value) {
        googleScroll.value.removeEventListener("scroll", onGoogleScroll);
      }
      if (videoScroll.value) {
        videoScroll.value.removeEventListener("scroll", onVideoScroll);
      }
    });

    return {
      resolvedContent,
      isVideoTabVisible,
      getEmbedUrl,
      activeTab,
      googleScroll,
      videoScroll,
      googleProgress,
      videoProgress,
      scroll,
    };
  },
};
</script>

<style scoped>
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
